import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { tcsFinancialData } from '../../data/tcsData.ts';
import { CompanyFinancialData } from '../../model/CompanyData.ts';
import './CompanyOverview.css';

const CompanyOverview: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'financials' | 'shareholding'>('overview');

  // For demo, we're using TCS data. Later this will fetch based on symbol
  const data: CompanyFinancialData = tcsFinancialData;

  const formatNumber = (num: number, isCurrency = true) => {
    if (isCurrency) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return num.toLocaleString('en-IN');
  };

  const formatCrores = (num: number) => {
    return `₹${(num / 100).toFixed(2)} Cr`;
  };

  return (
    <div className="company-overview-container">
      {/* Stock Header */}
      <div className="stock-header">
        <div className="stock-info-header">
          <h1>{data.stockInfo.companyName}</h1>
          <div className="stock-codes">
            <span className="stock-code">NSE: {data.stockInfo.nseCode}</span>
            <span className="stock-code">BSE: {data.stockInfo.bseCode}</span>
          </div>
        </div>
        <div className="stock-price-section">
          <div className="current-price">{formatNumber(data.stockInfo.currentPrice)}</div>
          <div className={`price-change ${data.stockInfo.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
            {data.stockInfo.priceChangePercent >= 0 ? '▲' : '▼'} {formatNumber(data.stockInfo.priceChange)} ({data.stockInfo.priceChangePercent}%)
          </div>
          <div className="last-updated">As of {data.stockInfo.lastUpdated}</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="overview-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'financials' ? 'active' : ''}`}
          onClick={() => setActiveTab('financials')}
        >
          Financials
        </button>
        <button
          className={`tab-button ${activeTab === 'shareholding' ? 'active' : ''}`}
          onClick={() => setActiveTab('shareholding')}
        >
          Shareholding
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-content">
          {/* Key Metrics Grid */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Market Cap</div>
              <div className="metric-value">{formatCrores(data.stockInfo.marketCap)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">52 Week High</div>
              <div className="metric-value">{formatNumber(data.stockInfo.weekHigh52)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">52 Week Low</div>
              <div className="metric-value">{formatNumber(data.stockInfo.weekLow52)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Face Value</div>
              <div className="metric-value">{formatNumber(data.stockInfo.faceValue)}</div>
            </div>
          </div>

          {/* Valuation Ratios */}
          <div className="section-card">
            <h2 className="section-title">Valuation Ratios</h2>
            <div className="ratio-grid">
              <div className="ratio-item">
                <span className="ratio-label">P/E Ratio</span>
                <span className="ratio-value">{data.valuationRatios.peRatio}</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">P/B Ratio</span>
                <span className="ratio-value">{data.valuationRatios.pbRatio}</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Book Value</span>
                <span className="ratio-value">{formatNumber(data.valuationRatios.bookValue)}</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">EV/EBITDA</span>
                <span className="ratio-value">{data.valuationRatios.evEbitda}</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Dividend Yield</span>
                <span className="ratio-value">{data.valuationRatios.dividendYield}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Price to Sales</span>
                <span className="ratio-value">{data.valuationRatios.priceToSales}</span>
              </div>
            </div>
          </div>

          {/* Profitability Metrics */}
          <div className="section-card">
            <h2 className="section-title">Profitability Metrics</h2>
            <div className="ratio-grid">
              <div className="ratio-item">
                <span className="ratio-label">ROE</span>
                <span className="ratio-value">{data.profitabilityMetrics.roe}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">ROE (3Y Avg)</span>
                <span className="ratio-value">{data.profitabilityMetrics.roe3Year}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">ROE (5Y Avg)</span>
                <span className="ratio-value">{data.profitabilityMetrics.roe5Year}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">ROCE</span>
                <span className="ratio-value">{data.profitabilityMetrics.roce}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Operating Margin</span>
                <span className="ratio-value">{data.profitabilityMetrics.operatingMargin}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Net Profit Margin</span>
                <span className="ratio-value">{data.profitabilityMetrics.netProfitMargin}%</span>
              </div>
            </div>
          </div>

          {/* Growth Metrics */}
          <div className="section-card">
            <h2 className="section-title">Growth Metrics</h2>
            <div className="ratio-grid">
              <div className="ratio-item">
                <span className="ratio-label">Sales Growth (3Y)</span>
                <span className="ratio-value">{data.growthMetrics.salesGrowth3Y}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Sales Growth (5Y)</span>
                <span className="ratio-value">{data.growthMetrics.salesGrowth5Y}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Profit Growth (3Y)</span>
                <span className="ratio-value">{data.growthMetrics.profitGrowth3Y}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Profit Growth (5Y)</span>
                <span className="ratio-value">{data.growthMetrics.profitGrowth5Y}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">Stock Price CAGR (3Y)</span>
                <span className="ratio-value">{data.growthMetrics.stockPriceCagr3Y}%</span>
              </div>
              <div className="ratio-item">
                <span className="ratio-label">EPS Growth</span>
                <span className="ratio-value">{data.growthMetrics.epsGrowth}%</span>
              </div>
            </div>
          </div>

          {/* Revenue Segments */}
          <div className="section-card">
            <h2 className="section-title">Revenue Segments ({data.revenueSegments[0]?.quarter})</h2>
            <div className="segments-list">
              {data.revenueSegments.map((segment, index) => (
                <div key={index} className="segment-item">
                  <div className="segment-info">
                    <span className="segment-name">{segment.segmentName}</span>
                    <span className="segment-contribution">{segment.contribution}%</span>
                  </div>
                  <div className="segment-bar">
                    <div
                      className="segment-bar-fill"
                      style={{ width: `${segment.contribution}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Corporate Actions */}
          <div className="section-card">
            <h2 className="section-title">Recent Corporate Actions</h2>
            <div className="corporate-actions-list">
              {data.corporateActions.map((action, index) => (
                <div key={index} className="action-item">
                  <div className="action-type-badge">{action.type}</div>
                  <div className="action-details">
                    <div className="action-description">{action.description}</div>
                    <div className="action-date">Date: {action.date}</div>
                    {action.exDate && <div className="action-date">Ex-Date: {action.exDate}</div>}
                    {action.amount && <div className="action-amount">Amount: ₹{action.amount}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Financials Tab */}
      {activeTab === 'financials' && (
        <div className="financials-content">
          {/* Quarterly Results */}
          <div className="section-card">
            <h2 className="section-title">Quarterly Results</h2>
            <div className="table-container">
              <table className="financial-table">
                <thead>
                  <tr>
                    <th>Quarter</th>
                    <th>Revenue</th>
                    <th>Operating Profit</th>
                    <th>Net Profit</th>
                    <th>EPS</th>
                    <th>YoY Growth</th>
                    <th>QoQ Growth</th>
                  </tr>
                </thead>
                <tbody>
                  {data.quarterlyResults.map((quarter, index) => (
                    <tr key={index}>
                      <td>{quarter.quarter}</td>
                      <td>{formatCrores(quarter.revenue)}</td>
                      <td>{formatCrores(quarter.operatingProfit)}</td>
                      <td>{formatCrores(quarter.netProfit)}</td>
                      <td>₹{quarter.eps}</td>
                      <td className={quarter.yoyGrowth >= 0 ? 'positive' : 'negative'}>
                        {quarter.yoyGrowth}%
                      </td>
                      <td className={quarter.qoqGrowth >= 0 ? 'positive' : 'negative'}>
                        {quarter.qoqGrowth}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Annual Results */}
          <div className="section-card">
            <h2 className="section-title">Annual Results</h2>
            <div className="table-container">
              <table className="financial-table">
                <thead>
                  <tr>
                    <th>Fiscal Year</th>
                    <th>Revenue</th>
                    <th>EBITDA</th>
                    <th>Net Profit</th>
                    <th>EPS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.annualResults.map((year, index) => (
                    <tr key={index}>
                      <td>{year.fiscalYear}</td>
                      <td>{formatCrores(year.revenue)}</td>
                      <td>{formatCrores(year.ebitda)}</td>
                      <td>{formatCrores(year.netProfit)}</td>
                      <td>₹{year.eps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Balance Sheet */}
          <div className="section-card">
            <h2 className="section-title">Balance Sheet</h2>
            <div className="table-container">
              <table className="financial-table">
                <thead>
                  <tr>
                    <th>Fiscal Year</th>
                    <th>Total Assets</th>
                    <th>Total Debt</th>
                    <th>Cash</th>
                    <th>Working Capital</th>
                    <th>Equity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.balanceSheet.map((bs, index) => (
                    <tr key={index}>
                      <td>{bs.fiscalYear}</td>
                      <td>{formatCrores(bs.totalAssets)}</td>
                      <td>{formatCrores(bs.totalDebt)}</td>
                      <td>{formatCrores(bs.cash)}</td>
                      <td>{formatCrores(bs.workingCapital)}</td>
                      <td>{formatCrores(bs.equity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cash Flow */}
          <div className="section-card">
            <h2 className="section-title">Cash Flow</h2>
            <div className="table-container">
              <table className="financial-table">
                <thead>
                  <tr>
                    <th>Fiscal Year</th>
                    <th>Operating Cash Flow</th>
                    <th>Investing Cash Flow</th>
                    <th>Financing Cash Flow</th>
                    <th>Free Cash Flow</th>
                    <th>Net Cash Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cashFlow.map((cf, index) => (
                    <tr key={index}>
                      <td>{cf.fiscalYear}</td>
                      <td className="positive">{formatCrores(cf.operatingCashFlow)}</td>
                      <td className="negative">{formatCrores(cf.investingCashFlow)}</td>
                      <td className="negative">{formatCrores(cf.financingCashFlow)}</td>
                      <td className="positive">{formatCrores(cf.freeCashFlow)}</td>
                      <td className={cf.netCashFlow >= 0 ? 'positive' : 'negative'}>
                        {formatCrores(cf.netCashFlow)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Shareholding Tab */}
      {activeTab === 'shareholding' && (
        <div className="shareholding-content">
          <div className="section-card">
            <h2 className="section-title">Shareholding Pattern</h2>
            <div className="table-container">
              <table className="financial-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Promoter Holding</th>
                    <th>FII Holding</th>
                    <th>DII Holding</th>
                    <th>Public Holding</th>
                    <th>Promoter Change</th>
                  </tr>
                </thead>
                <tbody>
                  {data.shareholdingPattern.map((sh, index) => (
                    <tr key={index}>
                      <td>{sh.date}</td>
                      <td>{sh.promoterHolding}%</td>
                      <td>{sh.fiiHolding}%</td>
                      <td>{sh.diiHolding}%</td>
                      <td>{sh.publicHolding}%</td>
                      <td className={sh.promoterChange >= 0 ? 'positive' : 'negative'}>
                        {sh.promoterChange >= 0 ? '+' : ''}{sh.promoterChange}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Shareholding Pie Chart (Text-based for now) */}
          <div className="section-card">
            <h2 className="section-title">Current Distribution ({data.shareholdingPattern[0]?.date})</h2>
            <div className="shareholding-bars">
              <div className="holding-bar-item">
                <span className="holding-label">Promoter</span>
                <div className="holding-bar">
                  <div
                    className="holding-bar-fill promoter"
                    style={{ width: `${data.shareholdingPattern[0]?.promoterHolding}%` }}
                  >
                    <span className="bar-text">{data.shareholdingPattern[0]?.promoterHolding}%</span>
                  </div>
                </div>
              </div>
              <div className="holding-bar-item">
                <span className="holding-label">FII</span>
                <div className="holding-bar">
                  <div
                    className="holding-bar-fill fii"
                    style={{ width: `${data.shareholdingPattern[0]?.fiiHolding}%` }}
                  >
                    <span className="bar-text">{data.shareholdingPattern[0]?.fiiHolding}%</span>
                  </div>
                </div>
              </div>
              <div className="holding-bar-item">
                <span className="holding-label">DII</span>
                <div className="holding-bar">
                  <div
                    className="holding-bar-fill dii"
                    style={{ width: `${data.shareholdingPattern[0]?.diiHolding}%` }}
                  >
                    <span className="bar-text">{data.shareholdingPattern[0]?.diiHolding}%</span>
                  </div>
                </div>
              </div>
              <div className="holding-bar-item">
                <span className="holding-label">Public</span>
                <div className="holding-bar">
                  <div
                    className="holding-bar-fill public"
                    style={{ width: `${data.shareholdingPattern[0]?.publicHolding}%` }}
                  >
                    <span className="bar-text">{data.shareholdingPattern[0]?.publicHolding}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOverview;
